import {
  type SerializedCompStructure,
  type SerializedPageStructure,
} from '@wix/document-services-types'

import { getVeloIdOf } from './getVeloIdOf'

const modifyComponentByVeloIdRecursively = <
  Root extends SerializedCompStructure | SerializedPageStructure,
>({
  root: root,
  veloId,
  modifyComponent,
}: {
  root: Root
  veloId: string
  modifyComponent: (
    component: SerializedCompStructure,
  ) => SerializedCompStructure
}): Root | null => {
  const { components } = root
  if (components !== undefined) {
    for (let index = 0; index < components.length; index += 1) {
      const component = components[index]
      const componentVeloId = getVeloIdOf(component)
      const modifiedComponent =
        componentVeloId === veloId
          ? modifyComponent(component)
          : modifyComponentByVeloIdRecursively({
              root: component,
              veloId,
              modifyComponent,
            })

      if (modifiedComponent !== null) {
        return {
          ...root,
          components: [
            ...components.slice(0, index),
            modifiedComponent,
            ...components.slice(index + 1),
          ],
        }
      }
    }
  }

  return null
}

export const modifyComponentByVeloId = <
  Root extends SerializedCompStructure | SerializedPageStructure,
>({
  root,
  veloId,
  modifyComponent,
}: {
  root: Root
  veloId: string
  modifyComponent: (
    component: SerializedCompStructure,
  ) => SerializedCompStructure
}): Root => {
  const modifiedParent = modifyComponentByVeloIdRecursively({
    root,
    veloId,
    modifyComponent,
  })
  if (modifiedParent === null) {
    throw new Error(`No component with Velo ID "${veloId}" found.`)
  }
  return modifiedParent
}
